import { AuthorDTO } from '@/types/AuthorDTO';
import { SenderDTO } from '@/types/SenderDTO';
import { getNumEnding, isBlank } from '@/utils/string-utils';

export const MESSAGE_REQUIRED = 'Обязательное поле';
export const MESSAGE_INVALID_CARD_CODE = 'Необходимо ввести 4-цифры';
export const MESSAGE_INVALID_EMAIL = 'Некорректный адрес электронной почты';
export const MESSAGE_INVALID_CREDENTIALS = 'Неверный адрес эл. почты/телефон или пароль';
export const MESSAGE_INVALID_LOGIN_IDENTIFIER = 'Введите корректный адрес эл. почты или телефон';
export const MESSAGE_USER_DISABLED = 'Адрес электронной почты не подтверждён';
export const MESSAGE_USER_DISABLED_PHONE_LOGIN =
  'Адрес электронной почты не подтверждён. Войдите по адресу эл. почты, чтобы отправить письмо повторно.';
export const MESSAGE_UNKNOWN = 'Неизвестная ошибка';
export const MESSAGE_FULL_NAME_REQUIRED = 'Укажите, как минимум, Фамилию и Имя';
export const MESSAGE_FULL_NAME_ALLOWED_CHARACTERS = 'Используйте только буквы кириллицы';
export const MESSAGE_ONLY_CYRILLIC_SYMBOLS = 'Используйте только буквы кириллицы';
export const MESSAGE_INVALID_LETTER_NUMBER = 'Номер должен состоять только из цифр';
export const MESSAGE_FETCH_ERROR = 'Ошибка соединения с сервером';
export const MESSAGE_PHONE_NOT_RUSSIAN = 'Можно использовать только российский номер';
export const MESSAGE_PHONE_INVALID = 'Некорректный номер телефона';
export const MESSAGE_INVALID_AMOUNT = 'Некорректная сумма';
export const MESSAGE_INVALID_ANSWERS_COUNT = 'Некорректное число ответов';
export const MESSAGE_PASSWORD_CAPITAL_LETTER = 'В пароле нет заглавных букв';
export const MESSAGE_PASSWORD_NUMBER = 'В пароле нет цифр';
export const MESSAGE_MIN_PASSWORD_LENGTH = 'Пароль меньше 8 символов';
export const MESSAGE_PASSWORD_NOT_EQUALS = 'Пароли не совпадают';
export const MESSAGE_WRONG_OLD_PASSWORD = 'Неверный текущий пароль';
export const MESSAGE_WRONG_PASSWORD = 'Неверный пароль';
export const MESSAGE_WRONG_EMAIL = 'Электронная почта уже зарегистрирована';
export const MESSAGE_EMAIL_LOCKED = 'Невозможно привязать электронную почту. Пожалуйста, укажите другую';
export const MESSAGE_EMAIL_NOT_EQUALS = 'Адреса электронной почты не совпадают';
export const MESSAGE_CAPTCHA_UNAVAILABLE = 'Сервис временно недоступен. Пожалуйста, попробуйте позже.';
export const MESSAGE_CAPTCHA_FAILURE = 'Не удалось выполнить действие. Пожалуйста, попробуйте ещё раз.';
export const MESSAGE_IMAGE_LOAD_FAILED = 'Не удалось загрузить изображение. Пожалуйста, попробуйте ещё раз.';
export const MESSAGE_STAGED_PHOTOS_EXPIRED =
  'Приложенные фотографии больше не хранятся на сервере. Приложите их заново.';

const SECONDS_IN_MINUTE = 60;

/**
 * Opens both forms of the throttle refusal, so rewording one does not leave the other behind.
 */
const MESSAGE_TOO_MANY_ATTEMPTS = 'Слишком много попыток входа.';

export const MESSAGE_LOGIN_THROTTLED = `${MESSAGE_TOO_MANY_ATTEMPTS} Пожалуйста, попробуйте позже.`;

/**
 * Names the wait in the form the count agrees with.
 *
 * @param value The number of units to wait.
 * @param endings The three forms of the unit, in the accusative case the phrasing takes.
 * @returns The refusal naming that wait.
 */
const formatRetryAfter = (value: number, endings: string[]): string =>
  `${MESSAGE_TOO_MANY_ATTEMPTS} Повторите попытку через ${value} ${getNumEnding(value, endings)}.`;

/**
 * Reports a sign-in the attempt throttle refused, naming the wait when the response carried one.
 * A `429` from the balancer carries no JSON body at all, so the message has to stand without a wait.
 * The wait is named in whole units rather than in the `M:SS` of `formatResendBlockMessage`,
 * because a snackbar shows the value once and cannot count it down.
 *
 * @param secondsRemaining The seconds before another attempt is permitted, absent for a balancer refusal.
 * @returns The message to show the user.
 */
export const formatLoginThrottled = (secondsRemaining?: number): string => {
  if (!secondsRemaining || secondsRemaining < 1) {
    return MESSAGE_LOGIN_THROTTLED;
  }
  if (secondsRemaining < SECONDS_IN_MINUTE) {
    return formatRetryAfter(secondsRemaining, ['секунду', 'секунды', 'секунд']);
  }
  // Rounded up, because naming a minute that has yet to elapse sends the user straight back into the refusal.
  return formatRetryAfter(Math.ceil(secondsRemaining / SECONDS_IN_MINUTE), ['минуту', 'минуты', 'минут']);
};

export const allowedFullNameCharsRegExp = /^[А-Яа-яЁё\s]+$/;

export const fullNameRegExp =
  /^\s*(?<lastName>[А-Яа-яёЁ-]+)\s+(?<firstName>[А-Яа-яёЁ-]+)(?<middleName>[А-Яа-яёЁ\s]+)?\s*$/;

export const cardCodeRegExp = /^\d{4}$/;

export const passwordCapitalLetterRegExp = /.*[A-Z]+.*/g;

export const passwordNumberRegExp = /.*[0-9]+.*/g;

export const MAX_LETTER_COUNT = 21840;

// The photo size limit the proxy and the servlet multipart config both enforce;
// checking it here refuses an oversized file before it is uploaded.
export const MAX_PHOTO_FILE_SIZE_BYTES = 20 * 1024 * 1024;

// The same limit in whole mebibytes, for user-facing texts.
export const MAX_PHOTO_FILE_SIZE_MB = MAX_PHOTO_FILE_SIZE_BYTES / (1024 * 1024);

export const SUPPORTED_PHOTO_FORMATS: readonly string[] = ['jpg', 'jpeg', 'png'];
const photoFormatLabels: string[] = SUPPORTED_PHOTO_FORMATS.map((format: string): string => format.toUpperCase());

// JPG and JPEG share the canonical MIME type used by file pickers.
export const PHOTO_FILE_ACCEPT = Array.from(
  new Set(SUPPORTED_PHOTO_FORMATS.map((format: string): string => `image/${format === 'jpg' ? 'jpeg' : format}`)),
).join(', ');

export const MESSAGE_PHOTO_SIZE_EXCEEDED = `Размер файла превышает допустимый лимит в ${MAX_PHOTO_FILE_SIZE_MB} МБ`;
export const MESSAGE_PHOTO_FORMAT_UNSUPPORTED = `Поддерживаются только изображения в форматах ${photoFormatLabels.slice(0, -1).join(', ')} и ${photoFormatLabels[photoFormatLabels.length - 1]}`;
export const PHOTO_UPLOAD_TOOLTIP = `Допустимые форматы: ${photoFormatLabels.join(', ')}. Размер файла — не более ${MAX_PHOTO_FILE_SIZE_MB} МБ.`;

// Fallback for the server-side `photoMaxCount` limit, used only until `ClientInfoDTO` is loaded.
export const DEFAULT_MAX_PHOTO_COUNT = 10;

export const validateAuthor = (author: AuthorDTO | null): boolean => {
  return !!author && !isBlank(author.name);
};

export const validateAuthorFull = (author: AuthorDTO | null): boolean => {
  return !!author && !isBlank(author.name) && !isBlank(author.phone) && !isBlank(author.email);
};

export const validateSender = (sender: SenderDTO | null): boolean => {
  return !!sender && !isBlank(sender.lastName) && !isBlank(sender.firstName);
};
